import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Input, Empty, Spin, Alert, Typography, Button, Segmented, Space, Tooltip } from 'antd';
import {
  SearchOutlined,
  RobotOutlined,
  CheckOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  UserOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import type { AgentDefinition } from '@/api/types';
import type { AgentSearchParams } from '@/api/agents';
import { trackEvent } from '@/lib/telemetry';
import { useAgentDirectory, useAgentRow } from './useAgentDirectory';
import { AgentStatusPill, AgentStatusToggle, statusParam, type AgentStatusFilter } from './agentStatus';

const { Text } = Typography;

// Reusable single-select agent picker. Used everywhere agents need to be
// chosen — Activity filter, Recent runs filter, Scenario library filter,
// future "assign workflow agent" flows. Mirrors MerchantPickerDrawer in
// shape: slide-in from the right, sticky search, click row → selects and
// closes. Adds three quick filters (audience / status / router) in the
// header so a roster of 100+ agents stays browsable without typing.
//
// The SERVER searches and filters (api/Agent/Search → AIAgentConfigManager.SearchDirectoryAsync,
// 2 Nov 2026): the picker used to filter the first page of agentRegistryStore in the browser, and a
// practice has more agents than one page (256 at practice 100) — "folfox" listed nothing. The text
// matches the names, canonical name, description and id, never the instruction body.
//
// Pair with `AgentPickerTrigger` for the canonical "Button → Drawer" pattern;
// or render the drawer directly if you need different trigger styling.

/** Rows per request; "Show more" asks for the next page of the same search. */
export const AGENT_PICKER_PAGE_SIZE = 50;

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  selectedId: number | null;
  /** Pass null to clear. The chosen row comes with it, so a caller never looks it up in a page it may not hold. */
  onSelect: (agentId: number | null, agent?: AgentDefinition) => void;
  // Optional pre-filter so callers can constrain the picker (e.g. only show
  // agents that participate in A2A). Runs on the rows the server returns.
  filterFn?: (agent: AgentDefinition) => boolean;
  title?: string;
  // Show the "All agents" / "Any agent" row at the top so the user can clear.
  allowClear?: boolean;
  clearLabel?: string;
  /** Also list the platform's agents (MerchantId null). */
  includeGlobal?: boolean;
  /** Never offer these (e.g. the agents a skill or prompt is already linked to): left out by the server, so counts are right. */
  excludeIds?: number[];
}

type AudienceQuick = 'all' | 'patient' | 'care';
type RouterQuick = 'all' | 'on' | 'off';

export const AgentPickerDrawer = observer(function AgentPickerDrawer({
  open,
  onClose,
  selectedId,
  onSelect,
  filterFn,
  title = 'Pick an agent',
  allowClear = true,
  clearLabel = 'Any agent',
  includeGlobal = false,
  excludeIds,
}: DrawerProps) {
  const { merchantContextStore, themeStore } = useStore();
  const merchantId = merchantContextStore.selectedMerchantId;
  const isDark = themeStore.mode === 'dark';
  const [query, setQuery] = useState('');
  const [audience, setAudience] = useState<AudienceQuick>('all');
  const [status, setStatus] = useState<AgentStatusFilter>('all');
  const [router, setRouter] = useState<RouterQuick>('all');
  const inputRef = useRef<{ focus: () => void } | null>(null);

  const request = useMemo<AgentSearchParams | null>(
    () =>
      !open || merchantId == null
        ? null
        : {
            merchantId,
            search: query.trim() || undefined,
            audience: audience === 'all' ? undefined : audience,
            status: statusParam(status),
            router: router === 'all' ? undefined : router,
            includeGlobal,
            excludeIds: excludeIds?.length ? excludeIds : undefined,
            pageSize: AGENT_PICKER_PAGE_SIZE,
          },
    [open, merchantId, query, audience, status, router, includeGlobal, excludeIds],
  );
  const { rows, total, loading, loadingMore, error, more, showMore } = useAgentDirectory(request);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 220);
    return () => clearTimeout(t);
  }, [open]);

  const visible = filterFn ? rows.filter(filterFn) : rows;
  const narrowed = !!request?.search || audience !== 'all' || status !== 'all' || router !== 'all';

  function handleSelect(agent: AgentDefinition | null) {
    onSelect(agent?.id ?? null, agent ?? undefined);
    trackEvent('AgentPicker.Selected', { agentId: agent?.id ?? -1 });
    onClose();
  }

  const headerBg = isDark ? colors.slate[800] : '#FFFFFF';
  const sectionBg = isDark ? colors.slate[900] : colors.slate[50];
  const borderColor = isDark ? colors.slate[700] : colors.slate[200];
  const quietColor = isDark ? colors.slate[400] : colors.slate[500];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={460}
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${borderColor}`, background: headerBg }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <RobotOutlined style={{ fontSize: 16, color: colors.ink[600] }} />
          <Text strong style={{ fontSize: 15 }}>{title}</Text>
          {total != null && (
            <span data-testid="agent-picker-count" style={{ marginLeft: 'auto', fontSize: 12, color: quietColor }}>
              {filterFn ? `${visible.length} shown` : `${rows.length} of ${total}`}
            </span>
          )}
        </div>

        <Input
          ref={inputRef as never}
          allowClear
          placeholder="Search by name, canonical name, or id…"
          prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={() => {
            if (!loading && visible.length === 1) handleSelect(visible[0]);
          }}
        />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          <Segmented<AudienceQuick>
            size="small"
            value={audience}
            onChange={(v) => setAudience(v)}
            options={[
              { label: <Tooltip title="Any audience"><span><TeamOutlined /> Any</span></Tooltip>, value: 'all' },
              { label: <Tooltip title="Exposed in patient app"><span><UserOutlined /> Patient</span></Tooltip>, value: 'patient' },
              { label: <Tooltip title="Exposed in care-team app"><span>Care</span></Tooltip>, value: 'care' },
            ]}
          />
          <AgentStatusToggle value={status} onChange={setStatus} />
          <Segmented<RouterQuick>
            size="small"
            value={router}
            onChange={(v) => setRouter(v)}
            options={[
              { label: <span><ThunderboltOutlined /> Router</span>, value: 'all' },
              { label: 'On', value: 'on' },
              { label: 'Off', value: 'off' },
            ]}
          />
          {(audience !== 'all' || status !== 'all' || router !== 'all') && (
            <Button
              size="small"
              type="text"
              icon={<ClearOutlined />}
              onClick={() => {
                setAudience('all');
                setStatus('all');
                setRouter('all');
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', background: sectionBg, padding: '8px 0' }}>
        {error && (
          <div style={{ padding: 16 }}>
            <Alert type="error" message="Failed to load agents" description={error} showIcon />
          </div>
        )}

        <Spin spinning={loading && rows.length > 0}>
          {loading && rows.length === 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Spin />
            </div>
          )}

          {!loading && !error && total === 0 && !narrowed && (
            <div style={{ padding: 24 }}>
              <Empty description="No agents in this practice" />
            </div>
          )}

          {allowClear && visible.length > 0 && (
            <AgentRow
              agent={null}
              isSelected={selectedId == null}
              isDark={isDark}
              onClick={() => handleSelect(null)}
              clearLabel={clearLabel}
            />
          )}

          {visible.map((a) => (
            <AgentRow
              key={a.id}
              agent={a}
              isSelected={a.id === selectedId}
              isDark={isDark}
              onClick={() => handleSelect(a)}
            />
          ))}

          {!loading && !error && visible.length === 0 && narrowed && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: quietColor }}>
              {request?.search ? `No agent matches “${request.search}” with these filters.` : 'No agents match your filters.'}
            </div>
          )}

          {more && !loading && (
            <div style={{ padding: '8px 20px', textAlign: 'center' }}>
              <Button size="small" loading={loadingMore} onClick={showMore}>
                Show more ({total! - rows.length} more)
              </Button>
            </div>
          )}
        </Spin>
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 20px', borderTop: `1px solid ${borderColor}`, background: headerBg }}>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Close</Button>
        </Space>
      </div>
    </Drawer>
  );
});

function AgentRow({
  agent,
  isSelected,
  isDark,
  onClick,
  clearLabel,
}: {
  agent: AgentDefinition | null;
  isSelected: boolean;
  isDark: boolean;
  onClick: () => void;
  clearLabel?: string;
}) {
  const [hover, setHover] = useState(false);
  const baseBg = isDark ? colors.slate[900] : colors.slate[50];
  const hoverBg = isDark ? colors.slate[800] : '#FFFFFF';
  const selectedBg = isDark ? colors.ink[700] + '33' : colors.ink[50];
  const bg = isSelected ? selectedBg : hover ? hoverBg : baseBg;
  const titleColor = isDark ? colors.slate[50] : colors.slate[900];
  const subColor = isDark ? colors.slate[400] : colors.slate[500];
  // The canonical name is the agent's identifier (a2a-identifier-conventions); the legacy A2A name only when there is none.
  const code = agent?.canonicalName || agent?.a2AAgentName;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 20px',
        cursor: 'pointer',
        background: bg,
        borderLeft: isSelected ? `3px solid ${colors.ink[600]}` : '3px solid transparent',
        transition: 'background 80ms',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {agent == null ? (
          <div style={{ fontSize: 13, fontWeight: 600, color: titleColor }}>
            {clearLabel ?? 'Any agent'}
          </div>
        ) : (
          // Mirror the merchant picker's row format — title (bold) +
          // the canonical name in mono on a second line. Filter pills move
          // to the header (Segmented controls), so each row stays clean
          // and the list scans fast even at 100+ agents.
          <>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: titleColor }}>
              {agent.displayName || `Agent #${agent.id}`}
            </div>
            {code && (
              <div
                style={{
                  fontSize: 11,
                  fontFamily: "'JetBrains Mono', monospace",
                  color: subColor,
                  marginTop: 2,
                }}
              >
                {code} · #{agent.id}
              </div>
            )}
          </>
        )}
      </div>
      {agent && <AgentStatusPill agent={agent} />}
      {isSelected && <CheckOutlined style={{ color: colors.ink[600], fontSize: 14 }} />}
    </div>
  );
}

// ── Convenience trigger: a Button that opens the drawer for you ────────────

interface TriggerProps {
  value: number | null;
  /** The chosen row comes with the id (absent when cleared). */
  onChange: (agentId: number | null, agent?: AgentDefinition) => void;
  placeholder?: string;
  filterFn?: (agent: AgentDefinition) => boolean;
  /** Never offer these (passed to the drawer, which leaves them out on the server). */
  excludeIds?: number[];
  // Drawer title override (defaults to "Pick an agent").
  drawerTitle?: string;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  allowClear?: boolean;
  clearLabel?: string;
}

export const AgentPickerTrigger = observer(function AgentPickerTrigger({
  value,
  onChange,
  placeholder = 'Any agent',
  filterFn,
  drawerTitle,
  size = 'middle',
  style,
  allowClear = true,
  clearLabel,
  excludeIds,
}: TriggerProps) {
  const [open, setOpen] = useState(false);
  // The chosen agent's row: the one the picker handed back, else read from the directory by id (a value set
  // elsewhere, e.g. restored from the URL).
  const [known, setKnown] = useState<AgentDefinition | null>(null);
  const selected = useAgentRow(value, known);
  const label = selected
    ? selected.displayName || `Agent #${selected.id}`
    : value != null
      ? `Agent #${value}`
      : placeholder;

  return (
    <>
      <Button
        size={size}
        icon={<RobotOutlined />}
        onClick={() => setOpen(true)}
        style={{
          minWidth: 180,
          textAlign: 'left',
          justifyContent: 'flex-start',
          fontWeight: value != null ? 500 : 400,
          ...style,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      </Button>
      <AgentPickerDrawer
        open={open}
        onClose={() => setOpen(false)}
        selectedId={value}
        onSelect={(id, agent) => {
          if (agent) setKnown(agent);
          onChange(id, agent);
        }}
        filterFn={filterFn}
        excludeIds={excludeIds}
        title={drawerTitle}
        allowClear={allowClear}
        clearLabel={clearLabel ?? placeholder}
      />
    </>
  );
});
